// What a parent says about each child's recovery shares (impire-hq
// 02-DESIGN/02-secrets/03-escrow.md § what the console shows, read through the
// child.<slug> verbs; 02-DESIGN/03-console/01-the-console.md § Settings: the
// Zero-trust tab, its last paragraph: "a parent's admin sees the same words
// for a child on the child's Zero-trust tab under Workspaces, with Collect and
// Present for Northwind's share and no New shares"). The child's row on
// Workspaces says the parent's share's state; the acts are the child's
// Zero-trust tab's (spec 061). The share is the parent's: *Northwind's share*
// of Acme's keys. Pure, so the words are tested as the design writes them.

import type { Tone } from "../../ui/components.tsx";
import {
  EscrowCode,
  PresentOutcome,
  Slot,
  SlotState,
  type EscrowPresenter,
  type EscrowSlotStatus,
  type EscrowStatus,
} from "../../wire/escrow.ts";
import { PLATFORM, presenterWords } from "../settings/zero-trust.ts";

/** ChildEscrow is what the row read for one child: the status, nothing where none answers, or a refusal. */
export interface ChildEscrow {
  status?: EscrowStatus;
  err?: unknown;
}

/** Who the share is between: the child's name and the parent's — the page's own workspace. */
export interface Between {
  child: string;
  parent: string;
}

/** parentSlot is the parent's share of the child's current set, where one exists. */
export function parentSlot(st: EscrowStatus | undefined): EscrowSlotStatus | undefined {
  if (st?.set === undefined) {
    return undefined;
  }
  return st.slots.find((s) => s.slot === Slot.parent);
}

/** own reads a presenter of the page's own workspace as its own person, without the workspace. */
function own(p: EscrowPresenter | undefined): EscrowPresenter | undefined {
  return p === undefined ? undefined : { ...p, workspace: undefined };
}

/**
 * childPresenterWords names a presenter on a child's tab as its shares table
 * names a collector: the platform's operator; the parent's own admin by name;
 * the child's people — no workspace, or the child's — as *an admin of Acme*.
 */
export function childPresenterWords(p: EscrowPresenter | undefined, b: Between, slug: string): string {
  if (p?.operator === true) {
    return `${PLATFORM}'s operator`;
  }
  if (p === undefined || p.workspace === undefined || p.workspace === "" || p.workspace === slug) {
    return `an admin of ${b.child}`;
  }
  return presenterWords(own(p), true);
}

/** RecoveryCell is the row's Recovery column: a pill and, where it helps, a line under it. */
export interface RecoveryCell {
  text: string;
  tone: Tone;
  sub?: string;
}

/** recoveryCell says the parent's share's state on a child's row, an open recovery first. */
export function recoveryCell(
  e: ChildEscrow | undefined,
  when: (iso: string | undefined) => string,
): RecoveryCell {
  if (e === undefined) {
    return { text: "reading", tone: "neutral" };
  }
  if (e.err !== undefined) {
    return { text: "not readable", tone: "bad" };
  }
  const st = e.status;
  if (st?.ceremony !== undefined) {
    return { text: "recovery in progress", tone: "info", sub: `Ends on ${when(st.ceremony.expires)}` };
  }
  const slot = parentSlot(st);
  if (slot === undefined) {
    return { text: "no shares yet", tone: "neutral" };
  }
  switch (slot.state) {
    case SlotState.waiting:
      return { text: "share waiting", tone: "info", sub: "Waiting for an admin to collect it" };
    case SlotState.collected:
      return {
        text: "share collected",
        tone: "ok",
        // The collector is one of the page's own admins: named, not *an admin of* the parent.
        sub: `By ${presenterWords(own(slot.collected_by), true)}${slot.collected !== undefined ? ` on ${when(slot.collected)}` : ""}`,
      };
    case SlotState.offline:
      return { text: "sealed offline", tone: "neutral" };
    default:
      return { text: `held by ${PLATFORM}`, tone: "neutral" };
  }
}

/** shareName is the parent's share of a child's keys: *Northwind's share of Acme's keys*. */
export function shareName(b: Between): string {
  return `${b.parent}'s share of ${b.child}'s keys`;
}

/** childCollectedLine sits under the one-time value. */
export function childCollectedLine(b: Between): string {
  return (
    `This is the only time ${shareName(b)} is shown. Whoever holds it and one other share can rebuild ` +
    `${b.child}'s keys — keep it where only you can reach it.`
  );
}

/** childPresentLine says when a parent's admin hands the share in. */
export function childPresentLine(b: Between): string {
  return (
    `Hand in ${shareName(b)} to rebuild them. You need this only when ${PLATFORM} or ${b.child}'s admins ` +
    `tell you ${b.child}'s keys were lost — after a restore, or a move to another installation. ` +
    `Never while ${b.child} runs normally.`
  );
}

/** childOutcome is the sentence for a present the service took. */
export function childOutcome(outcome: string, b: Between): string {
  if (outcome === PresentOutcome.recovered) {
    return `${b.child}'s keys are rebuilt. A new set of shares was issued; collect ${b.parent}'s below.`;
  }
  return `${shareName(b)} is in. The recovery completes when a second holder hands in theirs.`;
}

/** childRefusal is the sentence for a refused collect or present, or nothing for a code without one. */
export function childRefusal(err: unknown, b: Between): string | undefined {
  const code = typeof err === "object" && err !== null && "code" in err ? err.code : undefined;
  switch (code) {
    case EscrowCode.collected:
      return `${shareName(b)} was already collected by another of ${b.parent}'s admins.`;
    case EscrowCode.notFound:
      return `${b.child} is not a workspace ${b.parent} manages any more.`;
    default:
      return undefined;
  }
}

/** ZERO_TRUST_TAB is the child's page's tab for the parent's view of its shares. */
export const ZERO_TRUST_TAB = "zero-trust";

/**
 * childSlotWords says who holds each of a child's shares, from its parent's
 * side: the child's own share by its admins with no name — another
 * workspace's people are *an admin of Acme* — the parent's own share by the
 * parent's admin who collected it, named, and the platform's.
 */
export function childSlotWords(
  s: EscrowSlotStatus,
  b: Between,
  when: (iso: string | undefined) => string,
): string {
  const on = s.collected !== undefined ? ` on ${when(s.collected)}` : "";
  switch (s.slot) {
    case Slot.admin:
      if (s.state === SlotState.waiting) {
        return `Waiting for an admin of ${b.child} to collect it`;
      }
      return `Collected by an admin of ${b.child}${on}`;
    case Slot.parent:
      if (s.state === SlotState.waiting) {
        return "Waiting for an admin to collect it";
      }
      if (s.state === SlotState.offline) {
        return `Sealed, opened only from ${PLATFORM}'s safe`;
      }
      return `Collected by ${presenterWords(own(s.collected_by), true)}${on}`;
    default:
      return `Held by ${PLATFORM}`;
  }
}

/**
 * childReplacedLine says a newer set replaced an earlier one while the
 * parent's share of the new set waits: a copy collected before no longer works.
 */
export function childReplacedLine(
  st: EscrowStatus,
  b: Between,
  when: (iso: string | undefined) => string,
): string | undefined {
  if (st.cause === undefined || st.cause === "creation" || parentSlot(st)?.state !== SlotState.waiting) {
    return undefined;
  }
  return `These replace an earlier set: a copy of ${b.parent}'s share collected before ${when(st.made)} no longer works.`;
}

/** noChildShares is the tab's line where no set exists, or none the parent holds a share of. */
export function noChildShares(b: Between): string {
  return `No recovery shares have been issued for ${b.child} yet.`;
}

/** ChildTab is a tab of the child's page. */
export type ChildTab = "overview" | "blueprint" | typeof ZERO_TRUST_TAB;

/**
 * childFormTab is the tab a form open on the child's page stands in: a
 * Zero-trust form — `zero-trust/collect`, `zero-trust/present` — on
 * *Zero-trust*, any other on *Blueprint*, and none where no form is open.
 * A refresh with a form open opens its tab.
 */
export function childFormTab(open: string | undefined): ChildTab | undefined {
  if (open === undefined) {
    return undefined;
  }
  return open.startsWith(`${ZERO_TRUST_TAB}/`) ? ZERO_TRUST_TAB : "blueprint";
}

/**
 * childInitialTab is the tab the child's page opens on: a form's, or the
 * one a link carries in its navigation state — the Workspaces row's
 * *Recovery shares* opens *Zero-trust* — or *Overview*.
 */
export function childInitialTab(open: string | undefined, state: unknown): ChildTab {
  const tab = typeof state === "object" && state !== null && "tab" in state ? state.tab : undefined;
  return childFormTab(open) ?? (tab === ZERO_TRUST_TAB || tab === "blueprint" ? tab : "overview");
}

/** RECOVERY_LINK is the Workspaces row's entry that opens the child's page on its Zero-trust tab. */
export const RECOVERY_LINK = "Recovery shares";
